import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  db,
  collection,
  query,
  onSnapshot,
  httpsCallable,
  firebaseFunctions
} from '../services/firebaseService';
import { toUserError } from '../utils/callableError';
import { LocalizedText, OrgUnit } from '../types';
import { localizeOrgUnit, shortLanguage } from '../utils/orgLabels';
import { useTranslation } from 'react-i18next';

/** Höchste Ebene der Organisationsstruktur (0 = Wurzel, also 6 Ebenen). */
export const MAX_ORG_LEVEL = 5;

export interface CreateOrgUnitInput {
  names: LocalizedText;
  codes?: LocalizedText;
  description?: string;
  parentId: string | null;
}

export interface UpdateOrgUnitInput {
  orgUnitId: string;
  names?: LocalizedText;
  codes?: LocalizedText;
  description?: string;
  isActive?: boolean;
}

export interface InsertOrgRootInput {
  names: LocalizedText;
  codes?: LocalizedText;
  description?: string;
}

/** Folgen einer Verschiebung (docs/07, 2.4), vom Backend vor dem Verschieben ermittelt. */
export interface ReparentPreview {
  unitsMoved: number;
  levelChange: number;
  releases: { assessmentId: string; date: string | null }[];
  promotions: { removedOrgUnitIds: string[] }[];
}

/** Callable aufrufen; technische Fehler verständlich melden. */
async function callSafely<T>(invoke: () => Promise<T>): Promise<T> {
  try {
    return await invoke();
  } catch (err: unknown) {
    throw toUserError(err);
  }
}

/**
 * Baumreihenfolge: jede Einheit direkt unter ihrer übergeordneten, Geschwister nach Name.
 * So zeigen eingerückte Auswahllisten ("— — Steueramt") die Hierarchie korrekt.
 */
function sortInTreeOrder(units: OrgUnit[]): OrgUnit[] {
  const ids = new Set(units.map(u => u.id));
  const children = new Map<string | null, OrgUnit[]>();
  for (const unit of units) {
    const parent = unit.parentId && ids.has(unit.parentId) ? unit.parentId : null;
    children.set(parent, [...(children.get(parent) || []), unit]);
  }
  const ordered: OrgUnit[] = [];
  const visit = (parentId: string | null) => {
    for (const unit of (children.get(parentId) || []).sort((a, b) => a.name.localeCompare(b.name))) {
      ordered.push(unit);
      visit(unit.id);
    }
  };
  visit(null);
  // Nie Einheiten verlieren (z.B. bei fehlerhaften parentId-Zyklen): Rest hinten anhängen
  const placed = new Set(ordered.map(u => u.id));
  return [...ordered, ...units.filter(u => !placed.has(u.id))];
}

export function useOrgTree(tenantId?: string) {
  const { t, i18n } = useTranslation();
  const [rawUnits, setRawUnits] = useState<OrgUnit[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;

    // Auch deaktivierte Einheiten laden: ihre Namen werden für historische Daten gebraucht
    const q = query(collection(db, `tenants/${tenantId}/orgUnits`));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const units: OrgUnit[] = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        } as OrgUnit));

        setRawUnits(units);
        setIsLoading(false);
      },
      (err) => {
        console.error('Error listening to orgUnits:', err);
        setError(t('orgTree.fetchError', 'Fehler beim Laden der Organisationsstruktur.'));
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [tenantId, t]);

  // Namen und Kürzel in der Sprache der Person (docs/07, 2.2); `names`/`codes` bleiben zum Bearbeiten erhalten
  const lang = shortLanguage(i18n.language);
  const orgUnits = useMemo(() => sortInTreeOrder(rawUnits.map(u => localizeOrgUnit(u, lang))), [rawUnits, lang]);

  const orgUnitMap = useMemo(() => {
    const map = new Map<string, OrgUnit>();
    orgUnits.forEach((u) => map.set(u.id, u));
    return map;
  }, [orgUnits]);

  const getOrgUnit = useCallback((id: string): OrgUnit | undefined => {
    return orgUnitMap.get(id);
  }, [orgUnitMap]);

  const activeOrgUnits = useMemo(() => orgUnits.filter((u) => u.isActive !== false), [orgUnits]);

  const rootOrgUnit = useMemo(() => orgUnits.find((u) => u.parentId === null), [orgUnits]);

  const getChildren = useCallback((parentId: string | null): OrgUnit[] => {
    return orgUnits.filter((u) => u.parentId === parentId);
  }, [orgUnits]);

  const getSubtree = useCallback((id: string): OrgUnit[] => {
    return orgUnits.filter((u) => u.id === id || (u.ancestorIds && u.ancestorIds.includes(id)));
  }, [orgUnits]);

  const getAncestors = useCallback((id: string): OrgUnit[] => {
    const current = orgUnitMap.get(id);
    if (!current || !current.ancestorIds) return [];
    return current.ancestorIds
      .map((ancestorId) => orgUnitMap.get(ancestorId))
      .filter((u): u is OrgUnit => u !== undefined);
  }, [orgUnitMap]);

  const createOrgUnit = useCallback(async (input: CreateOrgUnitInput) => {
    if (!tenantId || !firebaseFunctions) throw new Error('Backend unavailable');
    const fn = httpsCallable(firebaseFunctions, 'createorgunit');
    const res = await callSafely(() => fn({ tenantId, ...input }));
    return res.data as { success: boolean; orgUnitId: string };
  }, [tenantId]);

  const updateOrgUnit = useCallback(async (input: UpdateOrgUnitInput) => {
    if (!tenantId || !firebaseFunctions) throw new Error('Backend unavailable');
    const fn = httpsCallable(firebaseFunctions, 'updateorgunit');
    const res = await callSafely(() => fn({
      tenantId,
      ...input
    }));
    return res.data as { success: boolean };
  }, [tenantId]);

  const previewReparent = useCallback(async (targetOrgUnitId: string, newParentId: string) => {
    if (!tenantId || !firebaseFunctions) throw new Error('Backend unavailable');
    const fn = httpsCallable(firebaseFunctions, 'previewreparentorgsubtree');
    const res = await callSafely(() => fn({ tenantId, targetOrgUnitId, newParentId }));
    return res.data as ReparentPreview;
  }, [tenantId]);

  const reparentOrgSubtree = useCallback(async (targetOrgUnitId: string, newParentId: string, confirmed = false) => {
    if (!tenantId || !firebaseFunctions) throw new Error('Backend unavailable');
    const fn = httpsCallable(firebaseFunctions, 'reparentorgsubtree');
    const res = await callSafely(() => fn({
      tenantId,
      targetOrgUnitId,
      newParentId,
      confirmed
    }));
    return res.data as { success: boolean; updatedCount: number };
  }, [tenantId]);

  const insertOrgRoot = useCallback(async (input: InsertOrgRootInput) => {
    if (!tenantId || !firebaseFunctions) throw new Error('Backend unavailable');
    const fn = httpsCallable(firebaseFunctions, 'insertorgroot');
    const res = await callSafely(() => fn({ tenantId, ...input }));
    return res.data as { success: boolean; orgUnitId: string };
  }, [tenantId]);

  const removeOrgRoot = useCallback(async () => {
    if (!tenantId || !firebaseFunctions) throw new Error('Backend unavailable');
    const fn = httpsCallable(firebaseFunctions, 'removeorgroot');
    const res = await callSafely(() => fn({ tenantId }));
    return res.data as { success: boolean; orgUnitId: string };
  }, [tenantId]);

  const deleteOrgUnit = useCallback(async (orgUnitId: string) => {
    if (!tenantId || !firebaseFunctions) throw new Error('Backend unavailable');
    const fn = httpsCallable(firebaseFunctions, 'deleteorgunit');
    const res = await callSafely(() => fn({
      tenantId,
      orgUnitId
    }));
    return res.data as { success: boolean };
  }, [tenantId]);

  return {
    orgUnits,
    activeOrgUnits,
    rootOrgUnit,
    orgUnitMap,
    isLoading,
    error,
    getOrgUnit,
    getChildren,
    getSubtree,
    getAncestors,
    createOrgUnit,
    updateOrgUnit,
    previewReparent,
    reparentOrgSubtree,
    insertOrgRoot,
    removeOrgRoot,
    deleteOrgUnit
  };
}
